import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, ImageOff, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useAuth } from '@/app/AuthProvider';
import { useProfiles } from '@/features/users/hooks';
import { useEquipmentList } from '@/features/equipment/hooks';
import {
  useDeleteEngPrint,
  useEngFileVersions,
  useEngFiles,
  useEngProjects,
  useEngSignedUrl,
  useSaveEngPrint,
  useUploadEngPrintPhoto,
  type EngPrint,
  type EngPrintValues,
} from '@/features/engineering/hooks';
import {
  ENG_PRINT_STATUS_OPTIONS,
  MAX_ENG_PHOTO_BYTES,
  PRINT_MATERIAL_SUGGESTIONS,
  formatBytes,
  formatPrintTime,
  isImageFile,
  parsePrintTime,
  type EngPrintStatus,
} from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { centsToInputDollars, inputDollarsToCents, todayIsoDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const NONE = '__none__';
const OTHER_PRINTER = '__other__';
const MATERIAL_LIST_ID = 'eng-print-material-suggestions';

export interface EngPrintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Known project (project tab). Omit on the global log to show a project picker. */
  projectId?: string;
  /** Existing print to edit; omit to log a new one. */
  print?: EngPrint | null;
  /** Pre-select a part file when logging from the Files tab. */
  defaultFileId?: string | null;
  canDelete?: boolean;
  onSaved?: (print: EngPrint) => void;
}

/**
 * Log or edit a 3D print. The form records what was printed (file +
 * version), on which printer, the slicer settings worth remembering,
 * how long it took and what it cost, how it went and what needs to
 * change for the next iteration — plus an optional photo.
 */
export function EngPrintDialog({
  open,
  onOpenChange,
  projectId,
  print,
  defaultFileId,
  canDelete = false,
  onSaved,
}: EngPrintDialogProps) {
  const { profile } = useAuth();
  const save = useSaveEngPrint();
  const remove = useDeleteEngPrint();
  const uploadPhoto = useUploadEngPrintPhoto();
  const { data: profiles } = useProfiles();
  const { data: equipment } = useEquipmentList();
  const needsProjectPicker = !projectId && !print;
  const { data: projects } = useEngProjects({ includeArchived: false });

  const [selectedProjectId, setSelectedProjectId] = useState<string>(projectId ?? print?.project_id ?? '');
  const effectiveProjectId = projectId ?? print?.project_id ?? selectedProjectId;
  const { data: files } = useEngFiles(effectiveProjectId || undefined);

  const [title, setTitle] = useState('');
  const [fileId, setFileId] = useState<string>(NONE);
  const [versionId, setVersionId] = useState<string>(NONE);
  const [printerChoice, setPrinterChoice] = useState<string>(NONE);
  const [printerName, setPrinterName] = useState('');
  const [material, setMaterial] = useState('');
  const [color, setColor] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [layerHeight, setLayerHeight] = useState('');
  const [infill, setInfill] = useState('');
  const [printTime, setPrintTime] = useState('');
  const [grams, setGrams] = useState('');
  const [cost, setCost] = useState('');
  const [status, setStatus] = useState<EngPrintStatus>('success');
  const [printedOn, setPrintedOn] = useState('');
  const [printedBy, setPrintedBy] = useState<string>(NONE);
  const [notes, setNotes] = useState('');
  const [changesNeeded, setChangesNeeded] = useState('');
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const { data: versions } = useEngFileVersions(fileId === NONE ? undefined : fileId);
  const { data: currentPhotoUrl } = useEngSignedUrl(print?.photo_path ?? null);

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSelectedProjectId(projectId ?? print?.project_id ?? '');
    setTitle(print?.title ?? '');
    setFileId(print?.file_id ?? defaultFileId ?? NONE);
    setVersionId(print?.file_version_id ?? NONE);
    if (print?.equipment_id) setPrinterChoice(print.equipment_id);
    else if (print?.printer_name) setPrinterChoice(OTHER_PRINTER);
    else setPrinterChoice(NONE);
    setPrinterName(print?.printer_name ?? '');
    setMaterial(print?.material ?? '');
    setColor(print?.color ?? '');
    setQuantity(String(print?.quantity ?? 1));
    setLayerHeight(print?.layer_height_mm == null ? '' : String(print.layer_height_mm));
    setInfill(print?.infill_percent == null ? '' : String(print.infill_percent));
    setPrintTime(print?.print_time_minutes == null ? '' : formatPrintTime(print.print_time_minutes));
    setGrams(print?.material_grams == null ? '' : String(print.material_grams));
    setCost(centsToInputDollars(print?.cost_cents));
    setStatus(print?.status ?? 'success');
    setPrintedOn(print ? (print.printed_on ?? '') : todayIsoDate());
    setPrintedBy(print ? (print.printed_by ?? NONE) : (profile?.id ?? NONE));
    setNotes(print?.notes ?? '');
    setChangesNeeded(print?.changes_needed ?? '');
    setPendingPhoto(null);
  }, [open, print, projectId, defaultFileId, profile?.id]);

  // Object URL for the staged photo preview.
  useEffect(() => {
    if (!pendingPhoto) {
      setPendingPreview(null);
      return;
    }
    const url = URL.createObjectURL(pendingPhoto);
    setPendingPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingPhoto]);

  // Default the version to the newest one whenever the file changes and
  // the current selection doesn't belong to it.
  useEffect(() => {
    if (fileId === NONE) {
      setVersionId(NONE);
      return;
    }
    if (!versions || versions.length === 0) return;
    if (versions.some((v) => v.id === versionId)) return;
    setVersionId(versions[0].id);
  }, [fileId, versions, versionId]);

  const partFiles = useMemo(() => {
    const list = (files ?? []).filter(
      (f) => f.category === 'cad' || f.category === 'mesh' || f.id === print?.file_id,
    );
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [files, print?.file_id]);

  const printers = useMemo(() => {
    const list = (equipment ?? []).filter(
      (e) => e.status !== 'retired' || e.id === print?.equipment_id,
    );
    // 3D printers live under "prototype"; surface those first.
    return list.sort((a, b) => {
      const ap = a.category === 'prototype' ? 0 : 1;
      const bp = b.category === 'prototype' ? 0 : 1;
      return ap - bp || a.name.localeCompare(b.name);
    });
  }, [equipment, print?.equipment_id]);

  const people = useMemo(
    () => (profiles ?? []).filter((p) => p.is_active !== false || p.id === print?.printed_by),
    [profiles, print?.printed_by],
  );

  const handleFileChange = (value: string) => {
    setFileId(value);
    if (value !== NONE && !title.trim()) {
      const f = partFiles.find((x) => x.id === value);
      if (f) setTitle(f.name.replace(/\.[^.]+$/, ''));
    }
  };

  const choosePhoto = (file: File | null) => {
    if (!file) return;
    if (!isImageFile(file.name) && !file.type.startsWith('image/')) {
      toast.error('Photos must be an image file.');
      return;
    }
    if (file.size > MAX_ENG_PHOTO_BYTES) {
      toast.error(`Photos are limited to ${formatBytes(MAX_ENG_PHOTO_BYTES)}.`);
      return;
    }
    setPendingPhoto(file);
  };

  const parseOptionalNumber = (raw: string, label: string, opts?: { integer?: boolean; min?: number; max?: number }) => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) throw new Error(`${label} must be a number.`);
    if (opts?.min != null && n < opts.min) throw new Error(`${label} can’t be below ${opts.min}.`);
    if (opts?.max != null && n > opts.max) throw new Error(`${label} can’t be above ${opts.max}.`);
    return opts?.integer ? Math.round(n) : n;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!effectiveProjectId) {
      toast.error('Pick the project this print belongs to.');
      return;
    }
    let values: EngPrintValues;
    try {
      const qty = parseOptionalNumber(quantity, 'Quantity', { integer: true, min: 1 }) ?? 1;
      values = {
        title,
        file_id: fileId === NONE ? null : fileId,
        file_version_id: fileId === NONE || versionId === NONE ? null : versionId,
        equipment_id: printerChoice === NONE || printerChoice === OTHER_PRINTER ? null : printerChoice,
        printer_name: printerChoice === OTHER_PRINTER ? printerName : null,
        material,
        color,
        quantity: qty,
        layer_height_mm: parseOptionalNumber(layerHeight, 'Layer height', { min: 0 }),
        infill_percent: parseOptionalNumber(infill, 'Infill', { integer: true, min: 0, max: 100 }),
        print_time_minutes: parsePrintTime(printTime),
        material_grams: parseOptionalNumber(grams, 'Material used', { min: 0 }),
        cost_cents: inputDollarsToCents(cost),
        status,
        printed_on: printedOn || null,
        printed_by: printedBy === NONE ? null : printedBy,
        notes,
        changes_needed: changesNeeded,
      };
    } catch (err) {
      toast.error(formatError(err));
      return;
    }
    try {
      const saved = await save.mutateAsync({ id: print?.id, projectId: effectiveProjectId, values });
      if (pendingPhoto) {
        try {
          await uploadPhoto.mutateAsync({ print: saved, file: pendingPhoto });
        } catch (err) {
          toast.error(formatError(err, 'The print was saved but the photo could not be uploaded.'));
        }
      }
      toast.success(print ? 'Print updated.' : 'Print logged.');
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err, 'Could not save the print.'));
    }
  };

  const handleDelete = async () => {
    if (!print) return;
    if (!window.confirm(`Delete the print “${print.title}”? This cannot be undone.`)) return;
    try {
      await remove.mutateAsync({ print });
      toast.success('Print deleted.');
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err, 'Could not delete the print.'));
    }
  };

  const busy = save.isPending || uploadPhoto.isPending || remove.isPending;
  const previewUrl = pendingPreview ?? currentPhotoUrl ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{print ? 'Edit print' : 'Log a 3D print'}</DialogTitle>
          <DialogDescription>
            Record what was printed, how it went and what should change next time. Material cost rolls into the
            project budget.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          {needsProjectPicker && (
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger aria-label="Engineering project">
                  <SelectValue placeholder="Choose a project" />
                </SelectTrigger>
                <SelectContent>
                  {(projects ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="font-mono text-xs text-muted-foreground">{p.code}</span> · {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <section className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-[1fr_1fr]">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="print-title">What was printed</Label>
                <Input
                  id="print-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Bowl rev C — lid test"
                  maxLength={200}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Part file</Label>
                <Select value={fileId} onValueChange={handleFileChange} disabled={!effectiveProjectId}>
                  <SelectTrigger aria-label="Part file">
                    <SelectValue placeholder="No file" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No file</SelectItem>
                    {partFiles.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {effectiveProjectId && partFiles.length === 0 && (
                  <p className="text-xs text-muted-foreground">Upload a CAD or mesh file to the project to link it here.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Version printed</Label>
                <Select value={versionId} onValueChange={setVersionId} disabled={fileId === NONE}>
                  <SelectTrigger aria-label="File version">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {fileId === NONE && <SelectItem value={NONE}>—</SelectItem>}
                    {(versions ?? []).map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        v{v.version_no}
                        {v.change_summary ? ` · ${v.change_summary}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Printer &amp; settings</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Printer</Label>
                <Select value={printerChoice} onValueChange={setPrinterChoice}>
                  <SelectTrigger aria-label="Printer">
                    <SelectValue placeholder="Not recorded" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not recorded</SelectItem>
                    {printers.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} <span className="font-mono text-xs text-muted-foreground">{e.asset_tag}</span>
                      </SelectItem>
                    ))}
                    <SelectItem value={OTHER_PRINTER}>Other (type a name)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {printerChoice === OTHER_PRINTER ? (
                <div className="space-y-1.5">
                  <Label htmlFor="print-printer-name">Printer name</Label>
                  <Input
                    id="print-printer-name"
                    value={printerName}
                    onChange={(e) => setPrinterName(e.target.value)}
                    placeholder="e.g. Jake's Bambu X1C"
                    maxLength={120}
                  />
                </div>
              ) : (
                <div className="hidden sm:block" />
              )}
              <div className="space-y-1.5">
                <Label htmlFor="print-material">Material</Label>
                <Input
                  id="print-material"
                  value={material}
                  onChange={(e) => setMaterial(e.target.value)}
                  list={MATERIAL_LIST_ID}
                  placeholder="PLA, PETG, resin…"
                  maxLength={80}
                />
                <datalist id={MATERIAL_LIST_ID}>
                  {PRINT_MATERIAL_SUGGESTIONS.map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="print-color">Color</Label>
                <Input
                  id="print-color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="e.g. Matte black"
                  maxLength={80}
                />
              </div>
            </div>
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor="print-qty">Quantity</Label>
                <Input
                  id="print-qty"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  inputMode="numeric"
                  min={1}
                  type="number"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="print-layer">Layer height (mm)</Label>
                <Input
                  id="print-layer"
                  value={layerHeight}
                  onChange={(e) => setLayerHeight(e.target.value)}
                  inputMode="decimal"
                  placeholder="0.2"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="print-infill">Infill (%)</Label>
                <Input
                  id="print-infill"
                  value={infill}
                  onChange={(e) => setInfill(e.target.value)}
                  inputMode="numeric"
                  placeholder="20"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="print-time">Print time</Label>
                <Input
                  id="print-time"
                  value={printTime}
                  onChange={(e) => setPrintTime(e.target.value)}
                  placeholder="2h 30m"
                />
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Outcome &amp; cost</h3>
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as EngPrintStatus)}>
                  <SelectTrigger aria-label="Print status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENG_PRINT_STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Printed on</Label>
                <DatePicker value={printedOn} onChange={setPrintedOn} placeholder="Date" aria-label="Printed on" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="print-grams">Material used (g)</Label>
                <Input
                  id="print-grams"
                  value={grams}
                  onChange={(e) => setGrams(e.target.value)}
                  inputMode="decimal"
                  placeholder="45"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="print-cost">Cost</Label>
                <Input
                  id="print-cost"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  inputMode="decimal"
                  placeholder="$ 0.00"
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Printed by</Label>
                <Select value={printedBy} onValueChange={setPrintedBy}>
                  <SelectTrigger aria-label="Printed by">
                    <SelectValue placeholder="Not recorded" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Not recorded</SelectItem>
                    {people.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.full_name ?? p.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Photo</Label>
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary',
                    )}
                  >
                    {previewUrl ? (
                      <img src={previewUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <ImageOff className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex min-w-0 flex-col gap-1">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        choosePhoto(e.target.files?.[0] ?? null);
                        e.target.value = '';
                      }}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => photoInputRef.current?.click()}>
                      <Camera className="mr-2 h-4 w-4" />
                      {previewUrl ? 'Replace photo' : 'Add photo'}
                    </Button>
                    {pendingPhoto && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => setPendingPhoto(null)}
                      >
                        <X className="h-3 w-3" />
                        <span className="truncate">{pendingPhoto.name}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="print-notes">How did it go?</Label>
                <Textarea
                  id="print-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Fit, finish, warping, supports, anything worth remembering…"
                  className="min-h-[96px]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="print-changes">What needs to change?</Label>
                <Textarea
                  id="print-changes"
                  value={changesNeeded}
                  onChange={(e) => setChangesNeeded(e.target.value)}
                  placeholder="Design changes for the next revision…"
                  className="min-h-[96px]"
                />
              </div>
            </div>
          </section>

          <DialogFooter className="gap-2 sm:justify-between">
            <div>
              {print && canDelete && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={handleDelete}
                  disabled={busy}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : print ? 'Save changes' : 'Log print'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
